// src/pages/DetallePropiedad.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import ImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import './DetallePropiedad.css';
import { obtenerDetalleInmueble } from '../services/inmueblesService';
import { obtenerTiposInmueble } from '../services/catalogosService';
import { crearProspecto } from '../services/prospectosService';
import { obtenerTour360PorInmueble } from '../services/tours360Service';
import { getCurrentUser, obtenerToken } from '../services/authService';
import { agregarFavorito, eliminarFavorito, existeFavorito } from '../services/favoritosService';
import RichTextContent from '../components/common/RichTextContent';
import Tour360Viewer from '../components/Tour360Viewer';
import { EVENTOS_CONVERSION, trackConversionEvent } from '../lib/conversionEvents';

const FORMULARIO_INICIAL = {
  nombre: '',
  telefono: '',
  email: '',
  mensaje: '',
};

const obtenerBasePublica = () => {
  const envUrl =
    process.env.REACT_APP_FRONTEND_PUBLIC_URL ||
    process.env.REACT_APP_SITE_URL ||
    process.env.REACT_APP_BASE_URL;

  if (envUrl) {
    return envUrl.replace(/\/+$/, '');
  }

  return typeof window !== 'undefined' ? window.location.origin : 'https://cninmobiliaria.com.mx';
};

const copiarTexto = async (texto) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(texto);
    return;
  }

  const textarea = document.createElement('textarea');
  textarea.value = texto;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.top = '-9999px';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
};

const getMensajeInteresPropiedad = (propiedad) => {
  if (propiedad?.modalidadOperacion === 'RENTA') {
    return `Hola, me interesa rentar la propiedad ${propiedad.titulo}.`;
  }

  if (propiedad?.modalidadOperacion === 'VENTA_RENTA') {
    return `Hola, me interesa conocer las opciones de venta o renta de la propiedad ${propiedad.titulo}.`;
  }

  return `Hola, me interesa conocer más sobre la propiedad en venta ${propiedad?.titulo || ''}.`;
};

export default function DetallePropiedad() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [propiedad, setPropiedad] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tour360, setTour360] = useState(null);
  const [tiposInmueble, setTiposInmueble] = useState([]);
  const [formulario, setFormulario] = useState(FORMULARIO_INICIAL);
  const [esFavorito, setEsFavorito] = useState(false);
  const [cargandoFavorito, setCargandoFavorito] = useState(false);
  const [mensajeFavorito, setMensajeFavorito] = useState('');
  const [enviandoProspecto, setEnviandoProspecto] = useState(false);
  const [errorProspecto, setErrorProspecto] = useState('');
  const [exitoProspecto, setExitoProspecto] = useState('');
  const [mensajeCompartir, setMensajeCompartir] = useState('');
  const conversionViewTrackedRef = useRef('');

  const token = obtenerToken();
  const usuario = token ? getCurrentUser() : null;
  const usuarioAutenticado = Boolean(token && usuario);

  useEffect(() => {
    const controller = new AbortController();

    const cargarDetalle = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await obtenerDetalleInmueble(id, { signal: controller.signal });
        setPropiedad(data);
      } catch (err) {
        if (err.name === 'AbortError') {
          return;
        }

        if (err.status === 404) {
          setError('Propiedad no encontrada.');
        } else {
          setError('No fue posible cargar el detalle de la propiedad.');
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    cargarDetalle();

    return () => controller.abort();
  }, [id]);

  useEffect(() => {
    const controller = new AbortController();

    const cargarTipos = async () => {
      try {
        const data = await obtenerTiposInmueble({ signal: controller.signal });
        if (!controller.signal.aborted) {
          setTiposInmueble(data);
        }
      } catch (_) {
        if (!controller.signal.aborted) {
          setTiposInmueble([]);
        }
      }
    };

    cargarTipos();

    return () => controller.abort();
  }, []);

  const tiposInmuebleMap = useMemo(
    () => new Map(tiposInmueble.map((tipo) => [String(tipo.id), tipo.nombre]).filter(([id, nombre]) => id && nombre)),
    [tiposInmueble]
  );
  const tipoInmuebleNombre =
    propiedad?.tipoInmuebleNombre ||
    tiposInmuebleMap.get(String(propiedad?.tipoInmueble)) ||
    'No especificado';


  useEffect(() => {
    if (!propiedad?.id) {
      conversionViewTrackedRef.current = '';
      return undefined;
    }

    const trackingKey = String(propiedad.id);
    if (conversionViewTrackedRef.current === trackingKey) {
      return undefined;
    }

    conversionViewTrackedRef.current = trackingKey;
    trackConversionEvent({
      tipoEvento: EVENTOS_CONVERSION.LANDING_VIEW,
      entidadTipo: 'PROPIEDAD',
      entidadId: propiedad.id,
      slug: String(propiedad.id || id),
      origen: 'landing',
      gaParams: { property_title: propiedad.titulo || '' },
    });

    return undefined;
  }, [id, propiedad?.id, propiedad?.titulo]);
  const shareData = useMemo(() => {
    if (!propiedad) {
      return null;
    }

    const propiedadId = propiedad.id || id;
    const shareUrl = `${obtenerBasePublica()}/#/propiedad/${propiedadId}`;
    const detalles = [];

    if (tipoInmuebleNombre && tipoInmuebleNombre !== 'No especificado') {
      detalles.push(tipoInmuebleNombre);
    }

    if (propiedad.ubicacion) {
      detalles.push(`en ${propiedad.ubicacion}`);
    }

    if (propiedad.precio) {
      detalles.push(propiedad.precio.replace(/\n/g, '. '));
    }

    const titulo = propiedad.titulo ? `: ${propiedad.titulo}` : '';
    const detalleTexto = detalles.length ? `. ${detalles.join(' ')}` : '';
    const shareTextWithoutUrl = `Encontré esta propiedad en CN Inmobiliaria${titulo}${detalleTexto}.`;
    const shareText = `${shareTextWithoutUrl} ${shareUrl}`;

    return {
      facebookUrl: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      shareText,
      shareTextWithoutUrl,
      shareUrl,
      whatsappUrl: `https://wa.me/?text=${encodeURIComponent(shareText)}`,
    };
  }, [id, propiedad, tipoInmuebleNombre]);

  useEffect(() => {
    const usuarioActual = getCurrentUser();

    if (!usuarioActual) {
      return;
    }

    setFormulario((actual) => ({
      ...actual,
      nombre: actual.nombre || usuarioActual.nombre || '',
      email: actual.email || usuarioActual.email || '',
    }));
  }, [id]);

  useEffect(() => {
    if (!obtenerToken()) {
      setEsFavorito(false);
      return undefined;
    }

    const controller = new AbortController();

    const cargarFavorito = async () => {
      try {
        const existe = await existeFavorito(id, { signal: controller.signal });
        if (!controller.signal.aborted) {
          setEsFavorito(existe);
        }
      } catch (_) {
        if (!controller.signal.aborted) {
          setEsFavorito(false);
        }
      }
    };

    cargarFavorito();

    return () => controller.abort();
  }, [id]);

  useEffect(() => {
    const controller = new AbortController();

    const cargarTour360 = async () => {
      try {
        const data = await obtenerTour360PorInmueble(id, { signal: controller.signal });
        if (!controller.signal.aborted) {
          setTour360(data);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setTour360(null);
        }
      }
    };

    setTour360(null);
    cargarTour360();

    return () => controller.abort();
  }, [id]);

  const obtenerIcono = (texto) => {
    const lower = texto
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    if (lower.includes('terreno')) return '\u{1F4D0}';
    if (lower.includes('construccion')) return '\u{1F3D7}';
    if (lower.includes('recamara')) return '\u{1F6CF}';
    if (lower.includes('recamaras')) return '\u{1F6CF}';
    if (lower.includes('bano')) return '\u{1F6C1}';
    if (lower.includes('estudio')) return '\u{1F9E0}';
    if (lower.includes('sala') || lower.includes('comedor')) return '\u{1F6CB}';
    if (lower.includes('cocina')) return '\u{1F37D}';
    if (lower.includes('terraza')) return '\u{1F305}';
    if (lower.includes('oficina')) return '\u{1F4BC}';
    if (lower.includes('vestidor')) return '\u{1F457}';
    if (lower.includes('cisterna')) return '\u{1F6B0}';
    if (lower.includes('roofgarden')) return '\u{1F33F}';
    if (lower.includes('credito')) return '\u{1F4B3}';
    if (lower.includes('min de')) return '\u{1F697}';
    if (lower.includes('lavado')) return '\u{1F9FA}';
    if (lower.includes('pasillo')) return '\u{1F6AA}';
    if (lower.includes('patio')) return '\u{1F33F}';
    if (lower.includes('amenidades')) return '\u{1F3CA}';
    return '\u{1F539}';
  };

  const obtenerTipoRecorrido = (visitaVirtual) =>
    (visitaVirtual?.tipo || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  const puedeEmbeberse = (url) => {
    if (!url) {
      return false;
    }

    return (
      /youtube\.com\/embed|youtube\.com\/watch|youtu\.be\/|vimeo\.com|matterport\.com|kuula\.co|roundme\.com/i.test(url)
    );
  };

  const esVisitaLegacyLocal = (url) => /(^|\/)visita\.html?$/i.test(url) || /(^|\/)visita\.hmtl$/i.test(url);

  const construirUrlEmbebida = (url) => {
    if (!url) {
      return '';
    }

    if (/youtube\.com\/watch\?v=([^&]+)/i.test(url)) {
      return url.replace(/watch\?v=/i, 'embed/');
    }

    if (/youtu\.be\/([^?]+)/i.test(url)) {
      const match = url.match(/youtu\.be\/([^?]+)/i);
      return match ? `https://www.youtube.com/embed/${match[1]}` : url;
    }

    if (/vimeo\.com\/(\d+)/i.test(url) && !/player\.vimeo\.com/i.test(url)) {
      const match = url.match(/vimeo\.com\/(\d+)/i);
      return match ? `https://player.vimeo.com/video/${match[1]}` : url;
    }

    return url;
  };

  const actualizarFormulario = (event) => {
    const { name, value } = event.target;

    setFormulario((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const enviarProspecto = async (event) => {
    event.preventDefault();

    if (!propiedad) {
      return;
    }

    try {
      setEnviandoProspecto(true);
      setErrorProspecto('');
      setExitoProspecto('');

      const notas = [
        `Interesado en inmueble ${propiedad.id}: ${propiedad.titulo}.`,
        `Operación: ${propiedad.modalidadOperacionLabel}.`,
        `Ubicacion: ${propiedad.ubicacion}.`,
        formulario.mensaje ? `Mensaje: ${formulario.mensaje}` : getMensajeInteresPropiedad(propiedad),
      ]
        .filter(Boolean)
        .join(' ');

      await crearProspecto({
        inmuebleId: Number(propiedad.id),
        nombre: formulario.nombre.trim(),
        telefono: formulario.telefono.trim(),
        email: formulario.email.trim(),
        origen: 'detalle_propiedad_web',
        notas,
      });

      setExitoProspecto(
        usuarioAutenticado
          ? 'Solicitud enviada. Puedes verla en Mis solicitudes.'
          : 'Tu solicitud fue enviada correctamente. Te contactaremos pronto.'
      );
      setFormulario((actual) => ({
        ...FORMULARIO_INICIAL,
        nombre: usuarioAutenticado ? actual.nombre : '',
        email: usuarioAutenticado ? actual.email : '',
      }));
    } catch (err) {
      setErrorProspecto(
        err.data?.mensaje ||
        err.data?.message ||
        'No fue posible enviar tu solicitud en este momento.'
      );
    } finally {
      setEnviandoProspecto(false);
    }
  };

  const alternarFavorito = async () => {
    if (!obtenerToken()) {
      navigate('/login', { state: { from: location } });
      return;
    }

    setCargandoFavorito(true);
    setMensajeFavorito('');

    try {
      if (esFavorito) {
        await eliminarFavorito(id);
      } else {
        await agregarFavorito(id);
      }

      setEsFavorito((actual) => !actual);
      setMensajeFavorito(esFavorito ? 'Propiedad quitada de favoritos.' : 'Propiedad guardada en favoritos.');
    } catch (err) {
      setMensajeFavorito(err.data?.mensaje || err.data?.message || 'No fue posible actualizar favoritos.');
    } finally {
      setCargandoFavorito(false);
    }
  };

  const copiarEnlacePropiedad = async () => {
    if (!shareData) {
      return;
    }

    try {
      await copiarTexto(shareData.shareUrl);
      setMensajeCompartir('Enlace copiado');
      window.setTimeout(() => setMensajeCompartir(''), 3000);
    } catch (_) {
      setMensajeCompartir('No fue posible copiar el enlace');
      window.setTimeout(() => setMensajeCompartir(''), 3000);
    }
  };

  const compartirNativo = async () => {
    if (!shareData || !navigator.share) {
      return;
    }

    try {
      await navigator.share({
        title: 'CN Inmobiliaria',
        text: shareData.shareTextWithoutUrl,
        url: shareData.shareUrl,
      });
    } catch (_) {
      // El usuario puede cancelar el dialogo nativo sin que sea un error de la pagina.
    }
  };

  if (loading) {
    return (
      <section className="detalle">
        <p className="estado-feedback">Cargando detalle de la propiedad...</p>
      </section>
    );
  }

  if (error || !propiedad) {
    return (
      <section className="detalle">
        <div className="estado-feedback estado-error">
          <p>{error || 'Propiedad no encontrada.'}</p>
          <Link to="/propiedades" className="btn-ver">Ver mas propiedades</Link>
        </div>
      </section>
    );
  }

  const galeriaFormateada = propiedad.imagenes.map((img) => ({
    original: img,
    thumbnail: img,
  }));
  const tipoRecorrido = obtenerTipoRecorrido(propiedad.visitaVirtual);
  const urlRecorrido = propiedad.visitaVirtual?.urlVisita || '';
  const tieneTour360 = Boolean(tour360?.escenas?.length);
  const visitaVirtualValida = propiedad.visitaVirtual && !esVisitaLegacyLocal(urlRecorrido);
  const recorridoEsVideo =
    tipoRecorrido === 'video' ||
    /\.(mp4|webm|ogg)$/i.test(urlRecorrido);
  const recorridoEmbebible =
    !recorridoEsVideo && puedeEmbeberse(urlRecorrido);
  const urlEmbebida = recorridoEmbebible ? construirUrlEmbebida(urlRecorrido) : '';

  return (
    <section className="detalle">
      {propiedad.video && (
        <div className="video">
          <video controls width="100%">
            <source src={propiedad.video} type="video/mp4" />
            Tu navegador no soporta el video.
          </video>
        </div>
      )}

      {galeriaFormateada.length > 0 ? (
        <div className="carrusel">
          <ImageGallery
            items={galeriaFormateada}
            showFullscreenButton={true}
            showPlayButton={false}
            thumbnailPosition="bottom"
          />
        </div>
      ) : (
        <div className="carrusel carrusel-vacio">
          <p className="estado-feedback">No hay imagenes disponibles para esta propiedad.</p>
        </div>
      )}

      <div className="contenido">
        <div className="detalle-titulo-row">
          <h1>{propiedad.titulo}</h1>
          <button
            type="button"
            className={`detalle-favorito-btn ${esFavorito ? 'is-active' : ''}`}
            onClick={alternarFavorito}
            disabled={cargandoFavorito}
          >
            <span>{esFavorito ? '♥' : '♡'}</span>
            {esFavorito ? 'Guardado' : 'Guardar'}
          </button>
        </div>
        {mensajeFavorito ? <p className="mensaje-favorito">{mensajeFavorito}</p> : null}
        <span className="detalle-operacion">{propiedad.modalidadOperacionLabel}</span>
        <div className="detalle-precios">
          {(propiedad.precioLineas?.length ? propiedad.precioLineas : [{ key: 'precio', text: propiedad.precio }]).map((linea) => (
            <p key={linea.key || linea.text} className="precio">{linea.text}</p>
          ))}
        </div>
        <p className="ubicacion">{'\u{1F4CD}'} {propiedad.ubicacion}</p>
        <p className="propiedad-tipo-inmueble"><strong>Tipo de inmueble:</strong> {tipoInmuebleNombre}</p>
        <RichTextContent className="descripcion" value={propiedad.descripcion} />

        {shareData ? (
          <div className="compartir-propiedad">
            <div className="compartir-propiedad-header">
              <h2>Compartir propiedad</h2>
              {mensajeCompartir ? <p>{mensajeCompartir}</p> : null}
            </div>
            <div className="compartir-propiedad-acciones">
              {typeof navigator !== 'undefined' && navigator.share ? (
                <button type="button" onClick={compartirNativo}>
                  Compartir
                </button>
              ) : null}
              <a
                href={shareData.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackConversionEvent({
                  tipoEvento: EVENTOS_CONVERSION.WHATSAPP_CLICK,
                  entidadTipo: 'PROPIEDAD',
                  entidadId: propiedad.id,
                  slug: String(propiedad.id || id),
                  origen: 'landing',
                  metadata: { boton: 'compartir_whatsapp' },
                })}
              >
                WhatsApp
              </a>
              <a href={shareData.facebookUrl} target="_blank" rel="noopener noreferrer">
                Facebook
              </a>
              <button type="button" onClick={copiarEnlacePropiedad}>
                Copiar enlace
              </button>
            </div>
          </div>
        ) : null}

        {tieneTour360 ? <Tour360Viewer tour={tour360} /> : null}

        {!tieneTour360 && visitaVirtualValida ? (
          <div className="recorrido-virtual">
            <div className="recorrido-encabezado">
              <h2>Recorrido virtual</h2>
              {propiedad.visitaVirtual.proveedor ? (
                <p>{`Proveedor: ${propiedad.visitaVirtual.proveedor}`}</p>
              ) : null}
            </div>

            {recorridoEsVideo ? (
              <div className="recorrido-media">
                <video controls width="100%">
                  <source src={urlRecorrido} type="video/mp4" />
                  Tu navegador no soporta el video.
                </video>
              </div>
            ) : recorridoEmbebible ? (
              <div className="recorrido-media">
                <iframe
                  title="Recorrido virtual"
                  src={urlEmbebida}
                  width="100%"
                  height="420"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                ></iframe>
              </div>
            ) : (
              <div className="recorrido-accion">
                <p>El recorrido virtual esta disponible en un enlace externo.</p>
                <a
                  href={urlRecorrido}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-recorrido"
                >
                  Abrir recorrido
                </a>
              </div>
            )}
          </div>
        ) : null}

        <ul className="caracteristicas">
          {propiedad.caracteristicas.map((carac, index) => {
            if (carac.tipo === 'simple') {
              return <li key={index}>{obtenerIcono(carac.texto)} {carac.texto}</li>;
            }

            if (carac.tipo === 'grupo') {
              return (
                <li key={index}>
                  {'\u2705'} <strong>{carac.titulo}:</strong>
                  <ul className="subcaracteristicas">
                    {carac.opciones.map((opcion, subIndex) => (
                      <li key={subIndex}>{obtenerIcono(opcion)} {opcion}</li>
                    ))}
                  </ul>
                </li>
              );
            }

            return null;
          })}
        </ul>

        {propiedad.latitud && propiedad.longitud ? (
          <div className="mapa">
            <iframe
              title="Ubicacion"
              src={`https://www.google.com/maps?q=${propiedad.latitud},${propiedad.longitud}&output=embed`}
              width="100%"
              height="300"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
            ></iframe>
          </div>
        ) : (
          <p>Ubicacion no disponible</p>
        )}

        <div className="prospecto-formulario">
          <div className="prospecto-formulario-encabezado">
            <div>
              <p>Solicitud</p>
              <h2>Solicitar información</h2>
            </div>
            {usuarioAutenticado ? (
              <Link to="/mis-solicitudes" className="prospecto-seguimiento-link">
                Mis solicitudes
              </Link>
            ) : (
              <Link
                to="/login"
                state={{ from: location }}
                className="prospecto-seguimiento-link"
              >
                Iniciar sesión
              </Link>
            )}
          </div>

          {usuarioAutenticado ? (
            <p className="prospecto-ayuda">
              Envia tu solicitud con tu cuenta para darle seguimiento desde Mis solicitudes.
            </p>
          ) : (
            <p className="prospecto-ayuda">
              Puedes llenar tus datos como visitante o iniciar sesión para dar seguimiento a tu solicitud.
            </p>
          )}

          <form onSubmit={enviarProspecto} className="formulario-contacto">
            <label className="campo-contacto">
              <span>Nombre</span>
              <input
                type="text"
                name="nombre"
                value={formulario.nombre}
                onChange={actualizarFormulario}
                required
                disabled={enviandoProspecto}
              />
            </label>

            <label className="campo-contacto">
              <span>Teléfono</span>
              <input
                type="tel"
                name="telefono"
                value={formulario.telefono}
                onChange={actualizarFormulario}
                required
                disabled={enviandoProspecto}
              />
            </label>

            <label className="campo-contacto">
              <span>Email</span>
              <input
                type="email"
                name="email"
                value={formulario.email}
                onChange={actualizarFormulario}
                required
                disabled={enviandoProspecto}
              />
            </label>

            <label className="campo-contacto campo-contacto-full">
              <span>Mensaje</span>
              <textarea
                name="mensaje"
                rows="4"
                value={formulario.mensaje}
                onChange={actualizarFormulario}
                disabled={enviandoProspecto}
                placeholder={getMensajeInteresPropiedad(propiedad)}
              ></textarea>
            </label>

            <div className="acciones-contacto">
              <button
                type="submit"
                className="btn-contacto"
                disabled={enviandoProspecto}
              >
                {enviandoProspecto ? 'Enviando...' : 'Solicitar información'}
              </button>
              {!usuarioAutenticado ? (
                <Link
                  to="/login"
                  state={{ from: location }}
                  className="btn-contacto btn-contacto-secundario"
                >
                  Iniciar sesión para seguimiento
                </Link>
              ) : null}
            </div>
          </form>

          {errorProspecto ? (
            <p className="mensaje-formulario mensaje-formulario-error">{errorProspecto}</p>
          ) : null}

          {exitoProspecto ? (
            <div className="mensaje-formulario mensaje-formulario-exito">
              <p>{exitoProspecto}</p>
              {usuarioAutenticado ? <Link to="/mis-solicitudes">Ver Mis solicitudes</Link> : null}
            </div>
          ) : null}
        </div>

        <div className="boton-ver">
          <Link to="/propiedades" className="btn-ver">Ver mas propiedades</Link>
        </div>
      </div>
    </section>
  );
}
